// Session persistence for shared/auth.
// See react-rewrite-plan.md sections 8.2 and 11.2 platform-auth-session.
//
// A Session describes the currently signed-in user from the frontend's point
// of view. The backend remains the source of truth; this cache just lets the
// UI render user-scoped chrome (name, email) without an extra round trip.

import { log } from '../logger';
import { createStorage, type SafeStorage } from '../storage';
import { AUTH_STORAGE_KEYS } from './keys';

// Lazy handle — see tokenStorage.ts for the rationale.
let cachedStorage: SafeStorage | null = null;
function getStorage(): SafeStorage {
  if (cachedStorage === null) {
    cachedStorage = createStorage('local');
  }
  return cachedStorage;
}

export function __resetSessionStorageForTests(): void {
  cachedStorage = null;
}

export interface SessionUser {
  id: string;
  email: string;
}

export interface Session {
  user: SessionUser;
  // Optional ISO-8601 timestamp. When absent, callers should treat the session
  // as having unknown expiry (still valid until the backend says otherwise).
  expiresAt?: string;
}

function isSessionUser(value: unknown): value is SessionUser {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const u = value as Record<string, unknown>;
  return typeof u.id === 'string' && typeof u.email === 'string';
}

function isSession(value: unknown): value is Session {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const s = value as Record<string, unknown>;
  if (!isSessionUser(s.user)) {
    return false;
  }
  if (s.expiresAt !== undefined && typeof s.expiresAt !== 'string') {
    return false;
  }
  return true;
}

export function getSession(): Session | null {
  const raw = getStorage().get<unknown>(AUTH_STORAGE_KEYS.session, null);
  if (!isSession(raw)) {
    if (raw !== null) {
      log.debug('shared/auth: stored session ignored (malformed)');
    }
    return null;
  }
  return raw;
}

export function setSession(session: Session): void {
  if (!isSession(session)) {
    log.debug('shared/auth: setSession ignored invalid value');
    return;
  }
  getStorage().set(AUTH_STORAGE_KEYS.session, session);
}

export function clearSession(): void {
  getStorage().remove(AUTH_STORAGE_KEYS.session);
}
